'use client'

/**
 * UserAvatar — ONE avatar rendering rule for the whole site (owner 2026-07-07: "profile
 * pictures should show the same across the site as on the profile page… include flairs").
 *
 * Renders through CleanProfileFrame so donor-tier flair frames apply exactly like the
 * profile page / navbar, plus the optional level badge. Callers pass the resolved image
 * URL (precedence profileImage > image > avatar — resolve in the query with COALESCE)
 * and donorTier/level when known. Missing images fall back to a deterministic gradient
 * initial (ringed with the tier color so flair still reads).
 * ⭐ THE TIER COMES FROM resolveDisplayTier (override > earned > none), NOT from a username. This
 * header used to read "TheDesperateTrader is always tier10 (creator), matching the navbar rule" and
 * the code matched it: a hardcoded string comparison, duplicated in three other components. The
 * grant now lives in users.tier_override. See lib/donor-utils.ts.
 */

import { useState } from 'react'
import CleanProfileFrame from '@/components/CleanProfileFrame'
import { resolveDisplayTier } from '@/lib/donor-utils'

const TONES = [
  'from-amber-500/80 to-orange-600/80', 'from-sky-500/80 to-blue-600/80',
  'from-emerald-500/80 to-teal-600/80', 'from-violet-500/80 to-purple-600/80',
  'from-rose-500/80 to-pink-600/80',
]
const toneFor = (name: string) => TONES[[...name].reduce((a, c) => a + c.charCodeAt(0), 0) % TONES.length]

const TIER_RING: Record<string, string> = {
  tier1: '#cd7f32', tier2: '#c0c0c0', tier3: '#3b82f6', tier4: '#10b981', tier5: '#06b6d4',
  tier6: '#8b5cf6', tier7: '#f97316', tier8: '#eab308', tier9: '#ec4899', tier10: '#ef4444',
}

export default function UserAvatar({
  src,
  name,
  size = 28,
  donorTier,
  tierOverride,
  level,
  className = '',
}: {
  src?: string | null
  name: string
  size?: number
  donorTier?: string | null
  /** Manual tier grant, outranks donorTier. Pass it wherever the query already selects the column. */
  tierOverride?: string | null
  level?: number | null
  className?: string
}) {
  const [broken, setBroken] = useState(false)
  const display = name || 'trader'
  /**
   * ⭐ SAME PRECEDENCE AS THE NAVBAR: override > earned > none (owner 2026-08-16).
   *
   * This was `display === 'TheDesperateTrader' ? 'tier10' : ...` - the second copy of a username
   * literal that also lived in Navbar.tsx. Two copies of a rule is how they drift, and a rename
   * would have silently stripped the frame from both.
   *
   * ⚠ CALLERS THAT DO NOT YET SELECT tier_override SIMPLY GET THE EARNED TIER, which is the correct
   * answer for all 999 accounts where the column is NULL. So this degrades to today's behaviour
   * everywhere except where the grant actually exists, rather than needing every query updated at
   * once. ⛔ But it does mean the owner's frame is only Sovereign on surfaces that pass it: the list
   * of callers is the work, and it is tracked in `navbar-identity-flair-and-level-zero`.
   */
  const tier = resolveDisplayTier(donorTier, tierOverride)
  const showLevel = typeof level === 'number' && level > 0 && size >= 26

  const badge = showLevel ? (
    <span
      className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full bg-amber-400 font-bold text-black shadow-sm ring-2 ring-card"
      style={{ width: Math.max(12, size * 0.42), height: Math.max(12, size * 0.42), fontSize: Math.max(7, size * 0.24) }}
    >
      {level}
    </span>
  ) : null

  if (src && !broken) {
    return (
      <span className={`relative inline-block flex-shrink-0 ${className}`} style={{ width: size, height: size }}>
        <span onError={() => setBroken(true)}>
          <CleanProfileFrame tier={tier} src={src} alt={display} width={size} height={size} />
        </span>
        {badge}
      </span>
    )
  }
  return (
    <span className={`relative inline-block flex-shrink-0 ${className}`} style={{ width: size, height: size }}>
      <span
        className={`flex items-center justify-center rounded-full bg-gradient-to-br font-black text-white ${toneFor(display)}`}
        style={{
          width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.42)),
          boxShadow: tier !== 'none' && TIER_RING[tier] ? `0 0 0 2px ${TIER_RING[tier]}` : undefined,
        }}
      >
        {display[0].toUpperCase()}
      </span>
      {badge}
    </span>
  )
}
